/**
 * Which task cards this operator wants on the workspace.
 *
 * IPL and Gift Nifty are seasonal — for most of the year they are noise on a
 * board whose whole job is "what still needs doing today". Hiding one takes its
 * card off the workspace and nothing else: the sidebar always lists all four,
 * so an off-season task is still one click away without going through the
 * picker first. Nothing is deleted — the data stays, and turning the card back
 * on shows it again.
 *
 * Stored per user at `admin_preferences/{uid}` so the choice follows the
 * operator rather than the browser.
 */

import { doc, getDoc, setDoc } from 'firebase/firestore';

export const TASKS = [
  { key: 'articles',  label: 'News Articles',       card: 'routeArticlesLabel' },
  { key: 'giftNifty', label: 'Gift Nifty',          card: 'routeGiftNiftyLabel' },
  { key: 'ipl',       label: 'IPL Manager',         card: 'routeIplLabel' },
  { key: 'gm',        label: 'Good Morning Images', card: 'routeGmLabel' },
];

const ALL = TASKS.map(t => t.key);
const STORAGE_KEY = 'ghoshna:visibleTasks';

/* Held in memory so the board can render before the network answers. */
let visible = [...ALL];

export function getVisibleTasks() {
  return [...visible];
}

export function isTaskVisible(key) {
  return visible.includes(key);
}

/** Keep only known keys, and never allow an empty board. */
function sanitise(keys) {
  const clean = (Array.isArray(keys) ? keys : []).filter(k => ALL.includes(k));
  return clean.length ? clean : [...ALL];
}

/**
 * Load the operator's choice.
 * Reads the local copy first so the workspace can paint immediately, then
 * reconciles with Firestore. Any failure falls back to showing everything —
 * a preference is never worth hiding work over.
 */
export async function loadTaskPreferences(db, uid) {
  try {
    const cached = localStorage.getItem(STORAGE_KEY);
    if (cached) visible = sanitise(JSON.parse(cached));
  } catch { /* a corrupt cache is not worth reporting */ }

  if (!db || !uid) return getVisibleTasks();

  try {
    const snap = await getDoc(doc(db, 'admin_preferences', uid));
    if (snap.exists() && Array.isArray(snap.data().visibleTasks)) {
      visible = sanitise(snap.data().visibleTasks);
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(visible)); } catch { /* ignore */ }
    }
  } catch (err) {
    console.warn('Could not read task preferences; showing all tasks.', err);
  }
  return getVisibleTasks();
}

/** Persist a new selection. Applies locally first so the UI never lags. */
export async function saveTaskPreferences(db, uid, keys) {
  visible = sanitise(keys);
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(visible)); } catch { /* ignore */ }

  if (!db || !uid) return { saved: false, visible: getVisibleTasks() };

  try {
    await setDoc(doc(db, 'admin_preferences', uid), { visibleTasks: visible }, { merge: true });
    return { saved: true, visible: getVisibleTasks() };
  } catch (err) {
    console.error('Could not save task preferences:', err);
    return { saved: false, visible: getVisibleTasks(), error: err };
  }
}

/**
 * Show or hide each task's card on the workspace. The sidebar is left alone —
 * hiding is about what the board reports, not about taking the page away.
 *
 * `hidden` rather than a class, so a hidden card leaves the accessibility tree
 * and the tab order too.
 */
export function applyTaskVisibility() {
  TASKS.forEach(({ key, card }) => {
    const on = visible.includes(key);
    const cardEl = document.getElementById(card);
    if (cardEl) cardEl.hidden = !on;

    // A hidden task must not keep the selection, or Continue would open
    // something the operator just chose not to see.
    if (!on && cardEl) {
      const radio = cardEl.querySelector('input[name="routeSelector"]');
      if (radio && radio.checked) {
        radio.checked = false;
        const first = TASKS.find(t => visible.includes(t.key));
        const firstRadio = first && document.getElementById(first.card)
          ?.querySelector('input[name="routeSelector"]');
        if (firstRadio) firstRadio.checked = true;
      }
    }
  });
}
